
import { Component, Vue, Watch, Mixins } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action } from "vuex-class";

import { Position } from "vue-router/types/router";
import { Guard, PatrolZone } from "@/store/dispatch/types";
import { catchError } from "rxjs/operators";
import { DispatchRequest, MobileFeatures, DispatchType, DeviceType, WebAppInterface, ScanType, GuardCheckedInLocation } from "@/store/mobile/types";
import { UserPermissions } from "@/store/types";
import { MobileRaiseProjection, MobileRaiseType } from "@/store/mobile-raise-templates/types";
import { MobileRaiseTemplateProjection } from "@/store/mobile-raise-templates/types";

import MobileMenu from "@/components/mobile/core/MobileMenu.vue";
import DispatchMap from "@/components/mobile/core/DispatchMap.vue";
import RightSideBar from "@/components/mobile/core/RightSideBar.vue";
import LeftSideBar from "@/components/mobile/core/LeftSideBar.vue";
import NotificationBar from "@/components/mobile/core/NotificationBar.vue";
import DispatchCore from "@/components/mobile/dispatch/DispatchCore.vue";
import ScanTypePicker from "@/components/mobile/dispatch/ScanTypePicker.vue";
import CameraPlayer from "@/components/mobile/live/CameraPlayer.vue";
import MobileViews from "@/components/mobile/views/MobileViews.vue";
import DispatchManageMap from "@/components/mobile/dispatch-manage/DispatchManageMap.vue";
import RaiseSelect from "@/components/mobile/mobile-raise/RaiseSelect.vue";
import RaiseActivityLog from "@/components/mobile/mobile-raise/RaiseActivityLog.vue";
import RaiseManualRaise from "@/components/mobile/mobile-raise/RaiseManualRaise.vue";
import ManualRaiseDetails from "@/components/mobile/mobile-raise/ManualRaiseDetails.vue";
import ChatButton from "@/components/mobile/messages/ChatButton.vue";
import GuardCheckInButton from "@/components/mobile/dispatch/GuardCheckInButton.vue";
import EnableGPSButton from "@/components/mobile/dispatch/EnableGPSButton.vue";
import ChatContainer from "@/components/mobile/messages/ChatContainer.vue";
import DuressButton from "@/components/mobile/core/DuressButton.vue";

// Mixins
import DispatchMixin from "@/mixins/DispatchMixin";
import { NewestAt } from '@/components/mobile/messages/NewestAt';
import MobileMixin from "@/mixins/MobileMixin";
import MobileEventQueueMap from "../events/MobileEventQueueMap.vue";

const Mobile = namespace("mobile");
const Dispatch = namespace("dispatch");
const SessionStore = namespace("sessions");
const MobileRaiseTemplates = namespace("mobileRaiseTemplates");

declare global {
	interface Window {
		notificationSettings: any;
		android: WebAppInterface;
	}
}

@Component({
	components: {
		"mobile-menu": MobileMenu,
		"dispatch-map": DispatchMap,
		"right-side-bar": RightSideBar,
		"left-side-bar": LeftSideBar,
		"notification-bar": NotificationBar,
		"dispatch-core": DispatchCore,
		"camera-player": CameraPlayer,
		"mobile-views": MobileViews,
		"dispatch-manage-map": DispatchManageMap,
		"raise-select": RaiseSelect,
		"raise-activity-log": RaiseActivityLog,
		"raise-manual-raise": RaiseManualRaise,
		"manual-raise-details": ManualRaiseDetails,
		"chat-button": ChatButton,
		"guard-check-in-button": GuardCheckInButton,
		"scan-type-picker": ScanTypePicker,
		"enable-gps-button": EnableGPSButton,
		"mobile-event-map": MobileEventQueueMap,
		"chat-container": ChatContainer,
		"duress-button": DuressButton
	}
})
export default class MobileCore extends Mixins(DispatchMixin, MobileMixin) {
	// Feature toggle
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;
	@Mobile.Getter("getMobileFeatures") getMobileFeatures: MobileFeatures;
	@Mobile.Getter("getHasArrived") hasArrived: boolean;
	@Getter getPermissions: UserPermissions;
	@Mobile.State(state => state.MainComponentName) mainComponentName: string;
	@Mobile.State(state => state.MapEnabled) mapEnabled: boolean;
	@Mobile.Mutation setMapEnabled: any;
	@Mobile.Mutation setNotificationText: any;
	@Mobile.Mutation setMobileMode: (mode: string) => void;

	// Availability
	@Mobile.Mutation setOnDuty: any;

	// Sessions
	@Mobile.Action extendMobileSession: any;
	@SessionStore.Action updateSession: any;
	@Mobile.Action mobileGuardLogout: any;

	// Maps
	@Mobile.Getter("getFocalPoint") focalPoint: any;
	@Mobile.State(state => state.DispatchGuard.manualLocationUpdate) manualLocationUpdate: boolean;

	// Dispatch Requests
	@Mobile.Getter("getDispatchRequests") dispatchRequests: DispatchRequest[];
	@Mobile.Getter("getActiveDispatchRequest") activeRequest: DispatchRequest;

	// Logout
	@Action serverLogout: any;
	@Action logout: () => void;

	// Left and right sidebars
	@Mobile.Getter("getIsRightSidebarOpen") isRightSidebarOpen: boolean;
	@Mobile.Getter("getIsLeftSidebarOpen") isLeftSidebarOpen: boolean;

	// Mobile Raise Templates
	@MobileRaiseTemplates.Action loadTemplateProjections: () => Promise<void>;
	@MobileRaiseTemplates.State(state => state.templateProjections) templates: MobileRaiseTemplateProjection[];
	@MobileRaiseTemplates.State(state => state.isProjectionsLoaded) isProjectionsLoaded: boolean;

	// Other vars
	private isLoggedOut: boolean = false;
	private toggleRightSidebar: boolean = false;
	private toggleLeftSidebar: boolean = false;

	private isRaiseSelectVisible = false;
	private isRaiseActivityLogVisible = false;
	private isRaiseManualRaiseVisible = false;
	private isManualRaiseDetailsVisible = false;
	private raise: MobileRaiseProjection = null;
	private createdEventId: number | null = null;
	private scanTypePickerEnabled: boolean = false;
	private guardLocationForManualUpdate: GuardCheckedInLocation = null;
	private isChatExpanded: boolean = false;

	private get isPrototype(): boolean {
		if (window.location.toString().startsWith("http://local")) {
			return true;
		} else {
			return false;
		}
	}

	public get isMobileEnabled() {
		return this.getFeature(["Mobile"]);
	}

	private get isMobileCheckInEnabled(): boolean {
		return this.getFeature(["Mobile", "CheckIn"]);
	}

	public async isTriggerMobileRaiseEnabled() {
		const featureEnabled = this.getFeature(["Mobile", "MobileRaise"]);
		if (featureEnabled)
		{
			// This stops the loadTemplateProjects from being called repeatedly
			if (this.templates.length > 0)
			{
				return true;
			}

			// Only check for templates if we have this permission (it requires it)
			if (this.getPermissions.canViewMobileRaise && !this.isProjectionsLoaded)
			{
				await this.loadTemplateProjections();
			}
		}

		return false;
	}

	async mounted() {
		// Fix for getting 100vh to calculate correctly on iOS
		window.scrollTo(0, document.body.scrollHeight);

		this.setMapEnabled(false); // default to map off

		if (this.isMobileEnabled) {
			// Extend the user session for the mobile user
			await this.updateSession({ resourceId: 6 });
			await this.extendMobileSession();
		}

		if (!window.location.protocol.toString().startsWith("https")) {
			this.setNotificationText("HTTPS must be enabled to use device location.");
		}
	}

	beforeDestroy() {
		if (!this.isLoggedOut) {
			this.setOnDuty(false);
		}
	}

	async logoutUser() {
		if (typeof window.notificationSettings === "undefined") {
			window.notificationSettings = { Id: "", DeviceType: DeviceType.Unknown };
		}

		await this.mobileGuardLogout({
			deviceId: window.notificationSettings.Id,
			deviceType: window.notificationSettings.DeviceType
		});

		this.setOnDuty(false);
		this.toggleLeftSidebar = !this.toggleLeftSidebar;

		// Actually log out
		await this.serverLogout();
		await this.logout();
		this.isLoggedOut = true;
	}

	// @techdebt - will be reintroduced in a future phase
	userLost() {
		if (this.isLoadedInApp()) {
			var scanPayload: ScanType = {
					type: "qr",
				};
			if (this.isiOS()) {
				(window as any).webkit.messageHandlers.scannerRequestHandler.postMessage(scanPayload);
			} else if (this.isAndroid()) {
				const scanPayloadSerialized = JSON.stringify(scanPayload);
				window.android.scanRequest(scanPayloadSerialized);
			}
		}	
		
	}

	private get dispatchListCount() {
		// DispatchRequestsCount - DispatchMixin Method
		return this.DispatchRequestsCount(this.dispatchRequests);
	}

	onRaise(raise: MobileRaiseProjection) {
		this.raise = raise;
		if (raise.manualRaiseType == MobileRaiseType.ActivityLog) {
			this.isRaiseActivityLogVisible = true;
		} else if (raise.manualRaiseType == MobileRaiseType.ManualRaise){
			this.isRaiseManualRaiseVisible = true;
		}
	}

	onSelfDispatch() {
		this.isManualRaiseDetailsVisible = false;
		this.setMobileMode("dispatch");
	}

	private get activeDispatchRequest() {
		return this.displayDispatchRequest(this.dispatchRequests).find(
			r => r.dispatchType === DispatchType.Dispatch && r.active
		);
	}

	private get isMobileCheckInVisible(): boolean {
		if (!this.isMobileCheckInEnabled || this.hasArrived) {
			return false;
		}

		return this.activeRequest != null;
	}

	private get isEnableGPSButtonVisible(): boolean {
		if (!this.isMobileEnabled || this.hasArrived) {
			return false;
		}

		return this.activeRequest == null && this.manualLocationUpdate;
	}

	private guardCheckedIn(guardLocation: GuardCheckedInLocation): void {
		this.guardLocationForManualUpdate = guardLocation;
		this.scanTypePickerEnabled = false;
	}

	private setChatExpanded(chatExpandedToggle: boolean) {
		this.isChatExpanded = chatExpandedToggle;
	}

	private get raiseDuressEventFeatureAndPermissionEnabled(): boolean {
		if (this.getFeature(['Mobile', 'Duress']) && 
			this.getPermissions.canRaiseDuress !== undefined && 
			this.getPermissions.canRaiseDuress === true) {
				return true;
		}

		return false;
	}
}
